import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { applyTheme } from "../lib/theme.js";
import { PublicDocumentLayout, PublicMessage } from "../components/public/PublicDocumentLayout.jsx";
import { ScopeTable } from "../components/public/ScopeTable.jsx";
import { AcceptanceForm } from "../components/public/AcceptanceForm.jsx";

/**
 * /sign-contract?token=… — public, no session (4b-4). States: loading, invalid
 * (404), expired (410), signed-already (409), declined (after Decline), review,
 * done (after signing). The email must match the signer contact; the API says so.
 */
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");
const fmtDateOnly = (d) => (d ? new Date(d).toLocaleDateString(undefined, { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" }) : "");

export function SignContract() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [declineErr, setDeclineErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setState({ kind: "invalid" }); return; }
      try {
        const body = await apiFetch(`/api/public/contracts/${encodeURIComponent(token)}`);
        if (cancelled) return;
        applyTheme(body.contract.accentColor);
        setState({ kind: "review", contract: body.contract });
      } catch (e) {
        if (cancelled) return;
        if (e.status === 410) setState({ kind: "expired", expiresAt: e.details?.expiresAt });
        else if (e.status === 409) setState({ kind: "signed", signedAt: e.details?.signedAt });
        else if (e.status === 429) setState({ kind: "busy" });
        else setState({ kind: "invalid" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function sign(form) {
    setBusy(true); setSubmitErr(null);
    try {
      const r = await apiFetch(`/api/public/contracts/${encodeURIComponent(token)}`, { method: "POST", body: JSON.stringify(form) });
      setState((s) => ({ kind: "done", name: form.name, signedAt: r.signedAt, contract: s.contract }));
    } catch (e) {
      if (e.status === 409) setState({ kind: "signed", signedAt: e.details?.signedAt });
      else if (e.status === 410) setState({ kind: "expired", expiresAt: e.details?.expiresAt });
      else setSubmitErr(e);
    } finally { setBusy(false); }
  }

  async function decline() {
    setBusy(true); setDeclineErr(null);
    try {
      await apiFetch(`/api/public/contracts/${encodeURIComponent(token)}/decline`, { method: "POST", body: JSON.stringify(reason.trim() ? { reason: reason.trim() } : {}) });
      setState((s) => ({ kind: "declined", contract: s.contract }));
    } catch (e) {
      if (e.status === 409) setState({ kind: "signed", signedAt: e.details?.signedAt });
      else if (e.status === 410) setState({ kind: "expired", expiresAt: e.details?.expiresAt });
      else if (e.status === 404) setState({ kind: "invalid" });
      else setDeclineErr(e);
    } finally { setBusy(false); }
  }

  const c = state.contract;
  const branding = c ? { divisionName: c.divisionName, logoUrl: c.logoUrl, accentColor: c.accentColor } : null;

  return (
    <PublicDocumentLayout branding={branding}>
      {state.kind === "loading" && <PublicMessage title="Loading agreement…"> </PublicMessage>}
      {state.kind === "invalid" && <PublicMessage title="This link is not valid">It may have been replaced or withdrawn. Contact Bayshore Marketing Group for a new one.</PublicMessage>}
      {state.kind === "busy" && <PublicMessage title="Too many requests">Please wait a few minutes and open the link again.</PublicMessage>}
      {state.kind === "expired" && <PublicMessage title="This signing link has expired">{state.expiresAt ? `It was valid until ${fmtDate(state.expiresAt)}. ` : ""}Reply to the email you received to request a new link.</PublicMessage>}
      {state.kind === "signed" && <PublicMessage title="Already signed">{state.signedAt ? `This agreement was signed on ${fmtDate(state.signedAt)}. ` : ""}Nothing more is needed from you.</PublicMessage>}
      {state.kind === "declined" && <PublicMessage title="Declined">Thanks for letting us know. {c?.divisionName} will follow up.</PublicMessage>}
      {state.kind === "done" && <PublicMessage title={`Signed. Thank you, ${state.name}.`}>{c.divisionName} will countersign and email you the executed agreement. Signed {fmtDate(state.signedAt)}.</PublicMessage>}
      {state.kind === "review" && (
        <div className="space-y-6">
          <div>
            <div className="text-[10px] uppercase font-medium text-text-muted" style={{ letterSpacing: "0.5px" }}>{c.typeLabel}</div>
            <h1 className="text-[20px] font-semibold text-text-primary mt-0.5">{c.title}</h1>
            <p className="text-[12px] text-text-secondary mt-1">
              Between <strong>{c.partyName}</strong> and {c.divisionName}{c.sentAt ? ` · sent ${fmtDate(c.sentAt)}` : ""}{c.signLinkExpiresAt ? ` · link valid until ${fmtDate(c.signLinkExpiresAt)}` : ""}
            </p>
            <a href={`/api/public/contracts/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-[12px] text-accent hover:underline">View the full agreement (PDF)</a>
          </div>

          <section className="rounded-card border border-border-default p-4 text-[13px] grid grid-cols-2 gap-y-2 gap-x-4">
            <div><div className="text-[10px] uppercase text-text-muted">Term</div><div>{c.termText}</div></div>
            <div><div className="text-[10px] uppercase text-text-muted">Effective</div><div>{c.effectiveDate ? fmtDateOnly(c.effectiveDate) : "Upon countersignature"}</div></div>
            <div><div className="text-[10px] uppercase text-text-muted">Signer</div><div>{c.signerName || "—"} · {c.signerEmailMasked}</div></div>
          </section>

          <section>
            <h2 className="text-[13px] font-semibold text-text-primary mb-2">Scope of services</h2>
            <ScopeTable lineItems={c.lineItems} />
          </section>

          <section className="rounded-card border border-border-default p-4 text-[13px] space-y-1.5" style={{ fontFeatureSettings: "'tnum'" }}>
            <h2 className="text-[13px] font-semibold text-text-primary mb-1">Investment</h2>
            {c.totals.setup > 0 && <div className="flex justify-between"><span className="text-text-secondary">One-time setup</span><span className="font-medium">{fmtMoney(c.totals.setup)}</span></div>}
            {c.totals.monthly > 0 && <div className="flex justify-between"><span className="text-text-secondary">Monthly recurring</span><span className="font-medium">{fmtMoney(c.totals.monthly)}/mo</span></div>}
            {c.totals.annual > 0 && <div className="flex justify-between"><span className="text-text-secondary">Annual recurring</span><span className="font-medium">{fmtMoney(c.totals.annual)}/yr</span></div>}
            {c.totals.setup === 0 && c.totals.monthly === 0 && c.totals.annual === 0 && <div className="text-text-muted">See line items for pricing.</div>}
          </section>

          {c.notes && (
            <section>
              <h2 className="text-[13px] font-semibold text-text-primary mb-1">Terms</h2>
              <p className="text-[13px] text-text-secondary whitespace-pre-line leading-relaxed">{c.notes}</p>
            </section>
          )}

          <section className="border-t border-border-light pt-5">
            <h2 className="text-[13px] font-semibold text-text-primary mb-1">Sign this agreement</h2>
            <p className="text-[12px] text-text-muted mb-3">Use the email address this agreement was sent to ({c.signerEmailMasked}).</p>
            <AcceptanceForm onSubmit={sign} busy={busy} error={submitErr}
              agreeLabel="I have read this agreement and agree to be bound by it. Typing my name below constitutes my electronic signature."
              buttonLabel="Sign agreement"
              nameLabel="Your full legal name"
              emailLabel={`Your email (must match ${c.signerEmailMasked})`} />
            {!declining ? (
              <button type="button" onClick={() => setDeclining(true)} className="mt-4 text-[12px] text-text-muted hover:text-negative">I need to decline this agreement</button>
            ) : (
              <div className="mt-4 space-y-2">
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder="Optional: tell us why, or what would need to change." className="w-full rounded border border-border-default px-2.5 py-2 text-[13px] bg-surface" />
                <div className="flex gap-2">
                  <button type="button" disabled={busy} onClick={decline} className="px-3 py-1.5 rounded-card border border-border-default text-[12px] text-negative disabled:opacity-50">Decline agreement</button>
                  <button type="button" onClick={() => setDeclining(false)} className="px-3 py-1.5 text-[12px] text-text-muted">Cancel</button>
                </div>
                {declineErr && <p className="text-[12px] text-negative">{declineErr.message}</p>}
              </div>
            )}
          </section>
        </div>
      )}
    </PublicDocumentLayout>
  );
}
